// File: js/reporting.js — the admin's reports and reconciliation controls.
//
// This module was the worst offender for silently wrong numbers. Every defect it
// carried is closed here, and each fix is named at the line that makes it:
//
//  • AUDIT C6 — NO READ IS UNBOUNDED. `supabase-js` caps at 1000 rows and returns
//    the truncated page WITHOUT an error; every sum computed over that array was
//    simply wrong, lower than reality, with no indication. At 500–1000 transactions
//    a month, any range past ~1–2 months was off, and an annual report by an order
//    of magnitude. Every read below goes through `fetchAllPages`, which stops ONLY
//    on a short page. Paginate or refuse (spec §8).
//
//  • AUDIT H1 — THE PERMANENT ONE-HOUR HOLE. The old bounds were
//    `new Date("YYYY-MM-DD").toISOString()` (UTC midnight = 01:00 WAT) against
//    `setHours(23,59,59,999)` (local), so the window was local [01:00 … 23:59:59]
//    and every sale taken between local 00:00 and 01:00 fell in NO report, ever.
//    All bounds now come from `money/business-date.js#businessDayBounds`, which is
//    HALF-OPEN — `>= startIso` and `< endIso` — the only shape that tiles with no
//    gap and no overlap.
//
//  • AUDIT H2 — ONE ARITHMETIC. Cash totals come from `money/money-fold.js`, the
//    same function the dashboard tiles and the cashier mini-stats fold with, so no
//    two screens can disagree about the same day. Where a server view supplies the
//    aggregate, BOTH figures are printed side by side under labels: a silent
//    divergence between the client fold and `v_daily_cash` is the single most
//    valuable thing this screen could ever tell an administrator.
//
//  • AUDIT M4 — « Résumé Journalier » ran the IDENTICAL query as the range report
//    and labelled the result with `startDate` alone, so a year of revenue could be
//    presented as one day's takings. The end date is now forced to the start date
//    and the field is disabled, so the label cannot lie.
//
//  • AUDIT L7 — `t.users.email` was dereferenced unguarded and one null join killed
//    the whole report. There are no PostgREST joins here at all: a separate,
//    paginated `id → email` map is consulted through `emailFor()`, which always
//    returns a string.
//
//  • AUDIT M7 — every interpolated value is escaped. A drug or patient name typed at
//    a cashier's desk renders here, in the ADMIN's session.
//
//  • AUDIT M2 / invariant 5 — integer FCFA throughout. No `parseFloat`, no `toFixed`.
//
// NEW: the reconciliation surfaces the engine makes possible — stock anomalies,
// receipt-number gaps and price mismatches. These are the controls the whole
// specification exists to provide, and a control nobody can see is worth nothing.

import { supabase } from './supabaseClient.js';
import { showStatus } from './utils.js';

import { db } from './db/schema.js';
import { fetchAllPages, pendingSummary } from './db/index.js';
import { messageForError } from './ui/engine-messages.js';
import { businessDayBounds, foldMoney, formatReceiptNo } from './money/index.js';

/**
 * Escape text destined for an `innerHTML` template (audit M7).
 * @param {*} value
 * @returns {string}
 */
function escapeHtml(value) {
    return String(value == null ? '' : value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

/**
 * Groupement fr-FR pour l'AFFICHAGE seulement.
 *
 * `utils.formatCurrency` (fr-CM, « 1 500 FCFA ») n'est pas touché : d'autres
 * écrans s'en servent et des tests l'épinglent. Les rapports composent le
 * nombre et l'unité séparément, exactement comme les écrans de caisse
 * redessinés, pour que « 142 500 F CFA » s'écrive partout de la même façon.
 * Aucun pliage n'est modifié : ceci ne touche que le texte affiché.
 */
const fmtNo = new Intl.NumberFormat('fr-FR');

/**
 * @param {*} value Entier FCFA (invariant 5 : jamais de décimale).
 * @returns {string} `142 500 F CFA`
 */
function fmtMoney(value) {
    const amount = Number(value);
    return `${fmtNo.format(Number.isFinite(amount) ? amount : 0)} F CFA`;
}

/**
 * L'état vide du document, qui GARDE SA FORME : un rapport non généré occupe la
 * même surface qu'un rapport généré et dit en toutes lettres ce qu'il attend.
 * Un panneau qui se replie à zéro laisse croire à une panne.
 * @param {string} title
 * @param {string} hint
 * @param {'neutral'|'danger'} [tone]
 * @returns {string}
 */
function placeholder(title, hint, tone = 'neutral') {
    return `
        <div class="report-placeholder${tone === 'danger' ? ' report-placeholder--danger' : ''}">
            <div class="report-placeholder-mark" aria-hidden="true">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.6" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
            </div>
            <p class="report-placeholder-title">${escapeHtml(title)}</p>
            <p class="report-placeholder-hint">${escapeHtml(hint)}</p>
        </div>`;
}

/** Report types whose window is exactly one business day (audit M4). */
const SINGLE_DAY_REPORTS = new Set(['daily_summary']);

/** Report types that have no date window at all — a position is a fold of ALL history. */
const UNDATED_REPORTS = new Set(['stock_level_report', 'stock_anomalies', 'receipt_gaps']);

/** French label for a ledger channel; `null` is the stock-movement group. */
const CHANNEL_LABEL = Object.freeze({
    regie: 'Régie (services de l\'État)',
    pharmacy: 'Pharmacie',
    unknown: 'Poste non identifié'
});

/**
 * @typedef {Object} RangeBounds
 * @property {string} fromDate `YYYY-MM-DD`, inclusive business date.
 * @property {string} toDate `YYYY-MM-DD`, inclusive business date.
 * @property {string} startIso First instant of `fromDate`, INCLUSIVE.
 * @property {string} endIso First instant of the day AFTER `toDate`, EXCLUSIVE.
 */

/**
 * Resolve the report window.
 *
 * `business_date` is a server-derived `date` (invariant 9), so a calendar-date
 * comparison against it is exact and needs no instants: `>= fromDate` and
 * `<= toDate` name the same half-open interval as `[startIso, endIso)` because a
 * date column has no time-of-day to fall between them. The INSTANT bounds are
 * carried alongside for the one case that needs them — an event that reaches a
 * projection carrying only a timestamp — and they are HALF-OPEN, because an
 * inclusive `lte` end bound is exactly how audit H1 lost an hour and would also
 * count a midnight-exact row on both days.
 *
 * `businessDayBounds` throws on a malformed or non-existent date (2026-02-31), so
 * a typo refuses here rather than silently producing an empty report.
 *
 * @param {string} startDate
 * @param {string} endDate
 * @returns {RangeBounds}
 */
function rangeBounds(startDate, endDate) {
    const start = businessDayBounds(startDate);
    const end = businessDayBounds(endDate);
    if (end.endIso <= start.startIso) {
        throw new Error('La date de fin doit être postérieure ou égale à la date de début.');
    }
    return { fromDate: startDate, toDate: endDate, startIso: start.startIso, endIso: end.endIso };
}

/**
 * Whether a ledger event's OWN business date falls in the window.
 *
 * The event's own date, deliberately — this screen answers "what did the
 * institution's cash position do over this period?", which is the question
 * `v_daily_cash` answers and which spec §10.1 says books a correction on the day
 * the correction was made. (The day-close fold asks the other question and
 * attributes to the TARGET sale's device-day. Two attributions, both correct;
 * every surface must declare which one it uses, and this one is declared here.)
 *
 * @param {object} event
 * @param {RangeBounds} bounds
 * @returns {boolean}
 */
function eventInRange(event, bounds) {
    const date = event && event.business_date;
    if (typeof date === 'string' && date !== '') {
        return date >= bounds.fromDate && date <= bounds.toDate;
    }
    // Fallback for a row that reached us with a timestamp but no derived date.
    // HALF-OPEN, always: `>=` start and `<` end (spec §8).
    const iso = (event && (event.device_time || event.server_received_at)) || null;
    if (typeof iso !== 'string' || iso === '') return false;
    return iso >= bounds.startIso && iso < bounds.endIso;
}

/** The ledger columns a report ever needs. Never `select('*')` on the ledger. */
const LEDGER_COLUMNS =
    'id, kind, channel, amount, reverses_id, reason, patient_name, receipt_no, '
    + 'collected_by, recorded_by, device_id, business_date, device_time, server_received_at, '
    + 'legacy_receipt_number';

export const Reporting = {
    elements: {},
    /** @type {Map<string, string>} user id → email (audit L7: never a join) */
    userEmails: new Map(),

    init() {
        this.elements = {
            modal: document.getElementById('reporting-modal'),
            closeBtn: document.getElementById('close-reporting-modal'),
            form: document.getElementById('report-form'),
            reportType: document.getElementById('report-type'),
            startDate: document.getElementById('report-start-date'),
            endDate: document.getElementById('report-end-date'),
            endDateGroup: document.getElementById('report-end-date-group'),
            endDateHint: document.getElementById('report-end-date-hint'),
            content: document.getElementById('report-content'),
            printBtn: document.getElementById('print-report-btn')
        };
        this.elements.openBtn = document.getElementById('generate-reports-btn');

        this.setupEventListeners();
        this.applyReportTypeConstraints();
        console.log('Reporting module initialized');
    },

    setupEventListeners() {
        this.elements.openBtn?.addEventListener('click', () => this.openModal());
        this.elements.closeBtn?.addEventListener('click', () => this.closeModal());
        this.elements.modal?.addEventListener('click', e => {
            if (e.target === this.elements.modal) this.closeModal();
        });
        this.elements.form?.addEventListener('submit', e => {
            e.preventDefault();
            void this.generateReport();
        });
        this.elements.reportType?.addEventListener('change', () => this.applyReportTypeConstraints());
        // Keeps « Résumé Journalier » honest while the user edits the start date
        // (audit M4): the end date follows it and can never widen the window.
        this.elements.startDate?.addEventListener('change', () => {
            if (SINGLE_DAY_REPORTS.has(this.elements.reportType?.value) && this.elements.endDate) {
                this.elements.endDate.value = this.elements.startDate.value;
            }
        });
        this.elements.printBtn?.addEventListener('click', () => window.print());
    },

    /**
     * Le nom survit à la fenêtre : les rapports occupent désormais l'écran
     * « Rapports » et non plus une boîte de dialogue. Cette méthode prépare le
     * formulaire ; `ui.js#openReportsModal` amène l'utilisateur sur le panneau.
     * La fenêtre `#reporting-modal` reste dans le document (coquille inerte) et
     * n'est plus jamais ouverte.
     */
    openModal() {
        const today = this.todayInputValue();
        if (this.elements.startDate && !this.elements.startDate.value) this.elements.startDate.value = today;
        if (this.elements.endDate && !this.elements.endDate.value) this.elements.endDate.value = today;
        this.applyReportTypeConstraints();
    },

    closeModal() {
        this.elements.modal?.classList.add('hidden');
    },

    /**
     * Today's business date (WAT, fixed +01:00), as a date-input value.
     * Not `new Date().toISOString().slice(0,10)`: that is the UTC day, and between
     * 23:00 and 00:00 WAT it names YESTERDAY.
     * @returns {string}
     */
    todayInputValue() {
        const now = new Date();
        // Shift into WAT, then read the calendar day in UTC — the same arithmetic
        // `money/business-date.js` performs, kept here only because this is a form
        // default rather than a money bound.
        const wat = new Date(now.getTime() + 60 * 60 * 1000);
        const pad = (n, w) => String(n).padStart(w, '0');
        return `${pad(wat.getUTCFullYear(), 4)}-${pad(wat.getUTCMonth() + 1, 2)}-${pad(wat.getUTCDate(), 2)}`;
    },

    /**
     * AUDIT M4, CLOSED. « Résumé Journalier » is ONE day by construction: the end
     * date is forced to the start date and its field is disabled, so a year of
     * revenue can no longer be presented under a single day's heading.
     */
    /**
     * Le rail de gauche et le `<select>` masqué disent forcément la même chose :
     * le rail POSITIONNE le select, le select reste la source de vérité, et cette
     * fonction — déjà appelée à l'initialisation et à chaque `change` — repeint
     * l'état actif. Aucun second état à synchroniser à la main.
     */
    syncReportTypeButtons() {
        const type = this.elements.reportType?.value || '';
        document.querySelectorAll('#reports-dashboard .report-type-btn').forEach(btn => {
            const on = btn.dataset.report === type;
            btn.classList.toggle('is-active', on);
            btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
    },

    applyReportTypeConstraints() {
        const type = this.elements.reportType?.value || '';
        const singleDay = SINGLE_DAY_REPORTS.has(type);
        const undated = UNDATED_REPORTS.has(type);

        this.syncReportTypeButtons();

        if (this.elements.endDate) {
            this.elements.endDate.disabled = singleDay || undated;
            if (singleDay && this.elements.startDate) {
                this.elements.endDate.value = this.elements.startDate.value;
            }
        }
        if (this.elements.startDate) this.elements.startDate.disabled = undated;

        if (this.elements.endDateHint) {
            this.elements.endDateHint.textContent = singleDay
                ? 'Le résumé journalier porte sur une seule journée : la date de fin suit la date de début.'
                : undated
                    ? 'Ce rapport porte sur la totalité de l\'historique : les dates ne s\'appliquent pas.'
                    : '';
            this.elements.endDateHint.classList.toggle('hidden', !singleDay && !undated);
        }
    },

    async generateReport() {
        const reportType = this.elements.reportType?.value;
        if (!reportType) {
            showStatus('Choisissez un type de rapport.', true);
            return;
        }

        this.elements.content.innerHTML = placeholder(
            'Génération du rapport…',
            'Le registre est lu page par page : un rapport partiel ne serait pas affiché.');
        this.elements.printBtn.classList.add('hidden');

        try {
            let bounds = null;
            if (!UNDATED_REPORTS.has(reportType)) {
                const startDate = this.elements.startDate?.value;
                // AUDIT M4: the daily report's window IS one day. Forced here too,
                // not only in the disabled input, so a stale DOM value cannot widen it.
                const endDate = SINGLE_DAY_REPORTS.has(reportType)
                    ? startDate
                    : (this.elements.endDate?.value || startDate);
                if (!startDate || !endDate) {
                    showStatus('Renseignez les dates du rapport.', true);
                    this.elements.content.innerHTML = placeholder(
                        'Rapport non généré',
                        'Renseignez la période, puis générez le rapport.');
                    return;
                }
                bounds = rangeBounds(startDate, endDate);
            }

            showStatus('Lecture des données…', false);
            await this.loadUserEmails();

            let html;
            switch (reportType) {
                case 'daily_summary':
                case 'date_range_summary':
                    html = await this.renderFinancialSummary(bounds, reportType);
                    break;
                case 'revenue_by_service':
                    html = await this.renderRevenueByService(bounds);
                    break;
                case 'transactions_by_cashier':
                    html = await this.renderTransactionsByCashier(bounds);
                    break;
                case 'stock_level_report':
                    html = await this.renderStockLevelReport();
                    break;
                case 'stock_anomalies':
                    html = await this.renderStockAnomalies();
                    break;
                case 'receipt_gaps':
                    html = await this.renderReceiptGaps();
                    break;
                case 'price_mismatches':
                    html = await this.renderPriceMismatches(bounds);
                    break;
                default:
                    throw new Error('Type de rapport inconnu.');
            }

            this.elements.content.innerHTML = html;
            this.elements.printBtn.classList.remove('hidden');
            showStatus('Rapport généré.', false);
        } catch (error) {
            console.error('Génération du rapport impossible:', error);
            // A PARTIAL FETCH REFUSES, IT NEVER RENDERS. `fetchAllPages` throws on any
            // page error, and that throw lands here: a report that could not be read
            // completely must show nothing rather than a plausible smaller number.
            this.elements.content.innerHTML = placeholder(
                'Rapport non généré',
                messageForError(error),
                'danger');
            showStatus('Rapport non généré — aucune donnée partielle n\'est affichée.', true);
        }
    },

    // ---- shared reads ------------------------------------------------------

    /**
     * The `id → email` map. A separate paginated read, never a PostgREST join:
     * `reporting.js:250` used to dereference `t.users.email` and one null join killed
     * the whole report (audit L7).
     */
    async loadUserEmails() {
        try {
            const rows = await fetchAllPages((from, to) => supabase
                .from('users')
                .select('id, email')
                .order('id', { ascending: true })
                .range(from, to));
            this.userEmails = new Map(rows.map(u => [u.id, u.email]));
        } catch (error) {
            console.warn('Liste des comptes indisponible:', error);
            this.userEmails = new Map();
        }
    },

    /**
     * @param {string|null|undefined} userId
     * @returns {string} Always a string. Never throws (audit L7).
     */
    emailFor(userId) {
        if (!userId) return 'compte inconnu';
        return this.userEmails.get(userId) || `compte inconnu (${String(userId).slice(0, 8)}…)`;
    },

    /**
     * Every row of a view or table, paginated to completion.
     * @param {string} relation
     * @param {{columns?:string, order:string[], filter?:(q:any)=>any}} options
     * @returns {Promise<object[]>}
     */
    async readAll(relation, options) {
        const columns = options.columns || '*';
        return fetchAllPages((from, to) => {
            let query = supabase.from(relation).select(columns);
            if (options.filter) query = options.filter(query);
            // Ordering is per-request in PostgREST and is never remembered between
            // pages; re-applied on every page by construction here.
            for (const col of options.order) query = query.order(col, { ascending: true });
            return query.range(from, to);
        });
    },

    /**
     * The sales of a window, plus EVERY correction ever recorded.
     *
     * The correction set is deliberately NOT date-filtered (spec §8): a `void`
     * carries the business date it was AUTHORED on — admin-only and online-only, so
     * by construction a different day from the cash it cancels. Filtering it by the
     * window would drop it from the set and a voided sale would silently re-count as
     * fully paid. Membership is decided fold-side, by `eventInRange`.
     *
     * @param {RangeBounds} bounds
     * @returns {Promise<{sales:object[], corrections:object[]}>}
     */
    async readCashEvents(bounds) {
        const [sales, corrections] = await Promise.all([
            this.readAll('money_ledger', {
                columns: LEDGER_COLUMNS,
                order: ['business_date', 'id'],
                filter: q => q.eq('kind', 'sale')
                    .gte('business_date', bounds.fromDate)
                    .lte('business_date', bounds.toDate)
            }),
            this.readAll('money_ledger', {
                columns: LEDGER_COLUMNS,
                order: ['id'],
                filter: q => q.in('kind', ['void', 'discount'])
            })
        ]);
        return { sales, corrections };
    },

    /**
     * The caveat every server-side total needs on a device that is holding cash the
     * server has never seen. Spec §9 requires unsynced money to be visible; this
     * report reads the SERVER, so the honest statement is that the queue is NOT in
     * these figures — the opposite caveat, and it must not be omitted.
     * @returns {Promise<string>}
     */
    async pendingCaveat() {
        try {
            const pending = await pendingSummary(db);
            if (!pending.count) return '';
            return `${pending.count} vente(s) de cet appareil (${fmtMoney(pending.total)}) `
                + 'ne sont pas encore synchronisées et NE FIGURENT PAS dans ce rapport.';
        } catch {
            return '';
        }
    },

    /**
     * @param {string} title
     * @param {string} period
     * @param {string} [caveat]
     * @param {string} [attribution]
     * @returns {string}
     */
    reportHeader(title, period, caveat, attribution) {
        return `
            <div class="report-header">
                <div class="report-brand">CMA OMBESSA</div>
                <h3 class="report-doc-title">${escapeHtml(title)}</h3>
                <div class="report-doc-meta">
                    <span>${escapeHtml(period)}</span>
                    <span class="report-doc-sep" aria-hidden="true">·</span>
                    <span>Généré le ${escapeHtml(new Date().toLocaleString('fr-FR'))}</span>
                </div>
            </div>
            ${caveat ? `
            <div class="report-callout report-callout--warn">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                </svg>
                <p>${escapeHtml(caveat)}</p>
            </div>` : ''}
            ${attribution ? `<div class="report-note"><p>${escapeHtml(attribution)}</p></div>` : ''}`;
    },

    /**
     * @param {RangeBounds} bounds
     * @param {boolean} singleDay
     * @returns {string}
     */
    periodLabel(bounds, singleDay) {
        const fr = d => new Date(`${d}T12:00:00Z`).toLocaleDateString('fr-FR');
        return singleDay
            ? `Journée du ${fr(bounds.fromDate)}`
            : `Période du ${fr(bounds.fromDate)} au ${fr(bounds.toDate)} (incluse)`;
    },

    // ---- 1. financial summary ---------------------------------------------

    /**
     * The cash position of a window.
     *
     * TWO INDEPENDENT ARITHMETICS ARE PRINTED SIDE BY SIDE, ON PURPOSE:
     *   • `v_daily_cash` — the server's own aggregate, which recovers a
     *     correction's CHANNEL from its target sale (spec §10.1) in SQL;
     *   • `foldMoney` — the client fold every other screen in this application
     *     uses, over the same membership.
     *
     * They must agree. Printing only one of them is how audit H2 happened — three
     * screens answering one question three ways, each convinced it was right. If
     * they ever diverge, the report says so in red rather than picking a winner.
     *
     * @param {RangeBounds} bounds
     * @param {string} reportType
     * @returns {Promise<string>}
     */
    async renderFinancialSummary(bounds, reportType) {
        const singleDay = SINGLE_DAY_REPORTS.has(reportType);
        const title = singleDay ? 'Résumé financier journalier' : 'Résumé financier par période';

        const [dailyCash, cashEvents, caveat] = await Promise.all([
            this.readAll('v_daily_cash', {
                order: ['business_date'],
                filter: q => q.gte('business_date', bounds.fromDate).lte('business_date', bounds.toDate)
            }),
            this.readCashEvents(bounds),
            this.pendingCaveat()
        ]);

        // --- the server aggregate ---
        const byChannel = new Map();
        let ignoredCount = 0;
        let ignoredAmount = 0;
        const totals = { gross: 0, voided: 0, discounted: 0, net: 0, count: 0 };

        for (const row of dailyCash) {
            ignoredCount += Number(row.ignored_event_count || 0);
            ignoredAmount += Number(row.ignored_amount || 0);
            if (!row.channel && !Number(row.cash_event_count || 0)) continue;

            const key = row.channel || 'unknown';
            const bucket = byChannel.get(key) || { gross: 0, voided: 0, discounted: 0, net: 0, count: 0 };
            bucket.gross += Number(row.gross_sales || 0);
            bucket.voided += Number(row.voided || 0);
            bucket.discounted += Number(row.discounted || 0);
            bucket.net += Number(row.net_cash || 0);
            bucket.count += Number(row.cash_event_count || 0);
            byChannel.set(key, bucket);

            totals.gross += Number(row.gross_sales || 0);
            totals.voided += Number(row.voided || 0);
            totals.discounted += Number(row.discounted || 0);
            totals.net += Number(row.net_cash || 0);
            totals.count += Number(row.cash_event_count || 0);
        }

        // --- the client fold, over the SAME membership (each event's own date) ---
        const inRange = cashEvents.sales
            .concat(cashEvents.corrections.filter(c => eventInRange(c, bounds)));
        const fold = foldMoney(inRange);

        const agrees = fold.net === totals.net
            && fold.gross === totals.gross
            && fold.voided === totals.voided
            && fold.discounted === totals.discounted;

        // Le tableau « Par poste » porte des nombres nus sous une note « Montants
        // en F CFA » : six colonnes qui répètent l'unité trente fois se lisent
        // moins bien que six colonnes de chiffres alignés.
        const channelRows = [...byChannel.entries()].map(([channel, b]) => `
            <tr>
                <td>${escapeHtml(CHANNEL_LABEL[channel] || channel)}</td>
                <td class="text-center">${escapeHtml(b.count)}</td>
                <td class="text-right">${escapeHtml(fmtNo.format(b.gross))}</td>
                <td class="text-right">${escapeHtml(fmtNo.format(b.discounted))}</td>
                <td class="text-right">${escapeHtml(fmtNo.format(b.voided))}</td>
                <td class="text-right"><strong>${escapeHtml(fmtNo.format(b.net))}</strong></td>
            </tr>`).join('') || '<tr><td colspan="6" class="text-center">Aucun mouvement de caisse sur la période.</td></tr>';

        const ignoredRows = fold.ignored.length > 0
            ? `<p class="report-hint">${escapeHtml(
                `${fold.ignored.length} événement(s) exclus des totaux de caisse par le pliage `
                + `(motifs : ${[...new Set(fold.ignored.map(i => i.reason))].join(', ')}).`)}</p>`
            : '';

        return `
            ${this.reportHeader(title, this.periodLabel(bounds, singleDay), caveat,
                'Attribution : une correction est comptée sur SA PROPRE date métier — '
                + 'la question posée est « qu\'a fait la position de caisse de l\'établissement sur la période ? ». '
                + 'La clôture de caisse répond à une autre question et attribue la correction à la journée de la vente d\'origine.')}

            <section class="report-section">
                <h4 class="report-section-title">Résumé global</h4>
                <div class="report-rows">
                    <div class="report-row">
                        <span class="report-row-label">Recettes brutes (ventes)</span>
                        <span class="report-row-value">${escapeHtml(fmtMoney(totals.gross))}</span>
                    </div>
                    <div class="report-row">
                        <span class="report-row-label">Remises</span>
                        <span class="report-row-value">−${escapeHtml(fmtMoney(totals.discounted))}</span>
                    </div>
                    <div class="report-row">
                        <span class="report-row-label">Annulations</span>
                        <span class="report-row-value">−${escapeHtml(fmtMoney(totals.voided))}</span>
                    </div>
                    <div class="report-net">
                        <span class="report-net-label">Recettes nettes</span>
                        <span class="report-net-value">${escapeHtml(fmtNo.format(totals.net))} <span class="report-net-unit">F CFA</span></span>
                    </div>
                    <p class="report-hint">${escapeHtml(
                        `${totals.count} opérations de caisse (ventes + corrections) · agrégat serveur v_daily_cash`)}</p>
                </div>
            </section>

            <div class="report-callout ${agrees ? 'report-callout--ok' : 'report-callout--danger'}">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    ${agrees
                        ? '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>'
                        : '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>'}
                </svg>
                <div>
                    <div class="report-callout-title">${agrees
                        ? 'Contrôle de cohérence : les deux calculs concordent'
                        : 'Contrôle de cohérence : DIVERGENCE — prévenez l\'administrateur, ne choisissez pas un chiffre'}</div>
                    <div class="report-callout-detail">${escapeHtml(
                        `Pliage client (money-fold.js) ${fmtMoney(fold.net)} · `
                        + `agrégat serveur (v_daily_cash) ${fmtMoney(totals.net)} · `
                        + `écart ${fmtMoney(fold.net - totals.net)}.`)}</div>
                </div>
            </div>
            ${ignoredRows}

            <section class="report-section">
                <h4 class="report-section-title">Par poste</h4>
                <div class="report-table-scroll">
                    <table>
                        <thead>
                            <tr>
                                <th>Poste</th>
                                <th class="text-center">Opérations</th>
                                <th class="text-right">Brut</th>
                                <th class="text-right">Remises</th>
                                <th class="text-right">Annulations</th>
                                <th class="text-right">Net</th>
                            </tr>
                        </thead>
                        <tbody>${channelRows}</tbody>
                        <tfoot>
                            <tr>
                                <td>Total</td>
                                <td class="text-center">${escapeHtml(totals.count)}</td>
                                <td class="text-right">${escapeHtml(fmtNo.format(totals.gross))}</td>
                                <td class="text-right">${escapeHtml(fmtNo.format(totals.discounted))}</td>
                                <td class="text-right">${escapeHtml(fmtNo.format(totals.voided))}</td>
                                <td class="text-right"><strong>${escapeHtml(fmtNo.format(totals.net))}</strong></td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
                <p class="report-hint">Montants en F CFA.</p>
            </section>

            <section class="report-section">
                <h4 class="report-section-title">Mouvements de stock</h4>
                <p class="report-hint report-hint--lead">Exclus des totaux de caisse, jamais supprimés.</p>
                <div class="report-rows">
                    <div class="report-row">
                        <span class="report-row-label">Réapprovisionnements et ajustements enregistrés</span>
                        <span class="report-row-value">${escapeHtml(ignoredCount)}</span>
                    </div>
                    <div class="report-row report-row--last">
                        <span class="report-row-label">Valeur d'inventaire correspondante (écriture, pas de la caisse)</span>
                        <span class="report-row-value">${escapeHtml(fmtMoney(ignoredAmount))}</span>
                    </div>
                </div>
            </section>`;
    },

    // ---- 2. revenue by service / item --------------------------------------

    /**
     * What was sold, by line.
     *
     * Lines come from `v_ledger_lines`, which explodes and TYPES the `items` array
     * server-side (and excludes the legacy pre-rewrite rows, whose stock was carried
     * in as one opening adjustment). The voided-sale set comes from an unfiltered
     * correction read, for the reason in `readCashEvents`.
     *
     * A DISCOUNT IS NOT ATTRIBUTED TO A LINE — it reduces the sale, not any
     * particular article — so it is deducted once, in the footer, and named. Spreading
     * it across lines would invent per-article revenue nobody ever charged.
     *
     * @param {RangeBounds} bounds
     * @returns {Promise<string>}
     */
    async renderRevenueByService(bounds) {
        const [lines, cashEvents, caveat] = await Promise.all([
            this.readAll('v_ledger_lines', {
                columns: 'event_id, kind, business_date, item_kind, item_id, item_name, qty, unit_price, line_total',
                order: ['event_id', 'line_index'],
                filter: q => q.eq('kind', 'sale')
                    .gte('business_date', bounds.fromDate)
                    .lte('business_date', bounds.toDate)
            }),
            this.readCashEvents(bounds),
            this.pendingCaveat()
        ]);

        const saleIds = new Set(cashEvents.sales.map(s => s.id));
        const voidedIds = new Set(
            cashEvents.corrections.filter(c => c.kind === 'void').map(c => c.reverses_id));
        const discountOnRange = cashEvents.corrections
            .filter(c => c.kind === 'discount' && saleIds.has(c.reverses_id) && !voidedIds.has(c.reverses_id))
            .reduce((sum, c) => sum + (Number.isInteger(c.amount) ? c.amount : 0), 0);

        /** @type {Map<string, {name:string, qty:number, total:number}>} */
        const byItem = new Map();
        let voidedLineTotal = 0;
        for (const line of lines) {
            const total = Number(line.line_total || 0);
            const qty = Number(line.qty || 0);
            if (voidedIds.has(line.event_id)) {
                voidedLineTotal += total;
                continue;
            }
            const key = line.item_id || `nom:${line.item_name || '?'}`;
            const bucket = byItem.get(key) || { name: line.item_name || 'Article non nommé', qty: 0, total: 0 };
            bucket.qty += qty;
            bucket.total += total;
            byItem.set(key, bucket);
        }

        const sorted = [...byItem.values()].sort((a, b) => b.total - a.total);
        const rows = sorted.map(item => `
            <tr>
                <td>${escapeHtml(item.name)}</td>
                <td class="text-center">${escapeHtml(item.qty)}</td>
                <td class="text-right">${escapeHtml(fmtMoney(item.total))}</td>
            </tr>`).join('')
            || '<tr><td colspan="3" class="text-center">Aucune ligne de vente sur la période.</td></tr>';

        const grandTotal = sorted.reduce((sum, item) => sum + item.total, 0);

        return `
            ${this.reportHeader('Recettes par service / article', this.periodLabel(bounds, false), caveat,
                'Les ventes annulées sont exclues. Une remise porte sur la vente entière et non sur une ligne : '
                + 'elle est déduite une seule fois, en pied de tableau.')}
            <table>
                <thead>
                    <tr>
                        <th>Service / article</th>
                        <th class="text-center">Quantité vendue</th>
                        <th class="text-right">Recettes brutes</th>
                    </tr>
                </thead>
                <tbody>${rows}</tbody>
                <tfoot>
                    <tr><td colspan="2">Total des lignes (hors ventes annulées)</td>
                        <td class="text-right">${escapeHtml(fmtMoney(grandTotal))}</td></tr>
                    <tr><td colspan="2">Remises accordées sur ces ventes</td>
                        <td class="text-right">−${escapeHtml(fmtMoney(discountOnRange))}</td></tr>
                    <tr><td colspan="2"><strong>Recettes nettes</strong></td>
                        <td class="text-right"><strong>${escapeHtml(fmtMoney(grandTotal - discountOnRange))}</strong></td></tr>
                    <tr><td colspan="3" class="text-xs text-muted">${escapeHtml(
                        `Exclu : ${fmtMoney(voidedLineTotal)} de lignes appartenant à des ventes annulées.`)}</td></tr>
                </tfoot>
            </table>`;
    },

    // ---- 3. transactions by cashier ----------------------------------------

    /**
     * Per collector: how much cash they took, net of the corrections against it.
     *
     * A correction is attributed to the COLLECTOR OF THE TARGET SALE, never to the
     * admin who authored it — the admin's browser never held that cash (spec §10.1).
     * The arithmetic is `foldMoney` per bucket, so a cashier's line here and the
     * same cashier's mini-stat tile cannot disagree (audit H2).
     *
     * @param {RangeBounds} bounds
     * @returns {Promise<string>}
     */
    async renderTransactionsByCashier(bounds) {
        const [{ sales, corrections }, caveat] = await Promise.all([
            this.readCashEvents(bounds),
            this.pendingCaveat()
        ]);

        const collectorOfSale = new Map(sales.map(s => [s.id, s.collected_by || null]));
        /** @type {Map<string, object[]>} collector id → the events that fold to their total */
        const buckets = new Map();
        const push = (collector, event) => {
            const key = collector || 'inconnu';
            const list = buckets.get(key) || [];
            list.push(event);
            buckets.set(key, list);
        };

        for (const sale of sales) push(sale.collected_by, sale);
        for (const correction of corrections) {
            if (!collectorOfSale.has(correction.reverses_id)) continue; // target outside the window
            push(collectorOfSale.get(correction.reverses_id), correction);
        }

        const rows = [...buckets.entries()]
            .map(([collector, events]) => {
                const fold = foldMoney(events);
                const saleCount = Object.values(fold.byChannel)
                    .reduce((sum, b) => sum + b.saleCount, 0);
                return { collector, fold, saleCount };
            })
            .sort((a, b) => b.fold.net - a.fold.net)
            .map(entry => `
                <tr>
                    <td>${escapeHtml(this.emailFor(entry.collector === 'inconnu' ? null : entry.collector))}</td>
                    <td class="text-center">${escapeHtml(entry.saleCount)}</td>
                    <td class="text-right">${escapeHtml(fmtMoney(entry.fold.gross))}</td>
                    <td class="text-right">−${escapeHtml(fmtMoney(entry.fold.discounted))}</td>
                    <td class="text-right">−${escapeHtml(fmtMoney(entry.fold.voided))}</td>
                    <td class="text-right"><strong>${escapeHtml(fmtMoney(entry.fold.net))}</strong></td>
                </tr>`).join('')
            || '<tr><td colspan="6" class="text-center">Aucune vente sur la période.</td></tr>';

        return `
            ${this.reportHeader('Transactions par caissier', this.periodLabel(bounds, false), caveat,
                'Une annulation ou une remise est portée au caissier qui a encaissé la vente d\'origine, '
                + 'jamais à l\'administrateur qui l\'a saisie (spéc. §10.1).')}
            <table>
                <thead>
                    <tr>
                        <th>Compte encaisseur</th>
                        <th class="text-center">Ventes</th>
                        <th class="text-right">Brut</th>
                        <th class="text-right">Remises</th>
                        <th class="text-right">Annulations</th>
                        <th class="text-right">Net</th>
                    </tr>
                </thead>
                <tbody>${rows}</tbody>
            </table>`;
    },

    // ---- 4. stock levels ---------------------------------------------------

    /**
     * Stock as `v_stock_positions` folds it — never a stored balance.
     *
     * A NEGATIVE POSITION IS PRINTED AS NEGATIVE AND FLAGGED. If two devices sold
     * the last unit offline, the goods have physically left the shelf: the books are
     * wrong, the shelf is not (spec §4). Clamping it to zero would invent stock and
     * hide the only signal there is.
     *
     * A DEACTIVATED DRUG IS STILL LISTED. `v_stock_positions` exposes `is_active` and
     * never filters on it: hiding a position is how inventory goes missing quietly.
     * @returns {Promise<string>}
     */
    async renderStockLevelReport() {
        const [positions, drugs] = await Promise.all([
            this.readAll('v_stock_positions', { order: ['drug_name', 'drug_id'] }),
            this.readAll('drugs', { columns: 'id, price, is_active', order: ['id'] })
        ]);
        const priceOf = new Map(drugs.map(d => [d.id, Number.isInteger(d.price) ? d.price : null]));

        let totalValue = 0;
        let negatives = 0;
        const rows = positions.map(p => {
            const position = Number(p.position || 0);
            const price = priceOf.has(p.drug_id) ? priceOf.get(p.drug_id) : null;
            const value = price === null ? null : position * price;
            if (value !== null) totalValue += value;
            if (position < 0) negatives += 1;

            const flag = position < 0
                ? ' style="color: var(--danger-600);"'
                : position === 0 ? ' style="color: var(--warning-700, #b45309);"' : '';
            return `
                <tr>
                    <td>${escapeHtml(p.drug_name)}${p.is_active === false ? ' <em>(désactivé)</em>' : ''}</td>
                    <td class="text-center"${flag}>${escapeHtml(`${position} ${p.unit || ''}`.trim())}</td>
                    <td class="text-right">${price === null ? '—' : escapeHtml(fmtMoney(price))}</td>
                    <td class="text-right">${value === null ? '—' : escapeHtml(fmtMoney(value))}</td>
                </tr>`;
        }).join('') || '<tr><td colspan="4" class="text-center">Aucun médicament.</td></tr>';

        return `
            ${this.reportHeader('Niveaux de stock', 'État de l\'inventaire pharmacie (pliage de la totalité du registre)',
                negatives > 0
                    ? `${negatives} position(s) négative(s) : voir le rapport « Anomalies de stock ».`
                    : '',
                'Le stock est un pliage d\'événements, jamais un solde stocké. Les positions négatives sont '
                + 'affichées telles quelles : une survente hors ligne est un fait, pas une erreur à masquer.')}
            <table>
                <thead>
                    <tr>
                        <th>Médicament</th>
                        <th class="text-center">Position</th>
                        <th class="text-right">Prix unitaire (liste)</th>
                        <th class="text-right">Valeur</th>
                    </tr>
                </thead>
                <tbody>${rows}</tbody>
                <tfoot>
                    <tr><td colspan="3"><strong>Valeur totale de l'inventaire</strong></td>
                        <td class="text-right"><strong>${escapeHtml(fmtMoney(totalValue))}</strong></td></tr>
                </tfoot>
            </table>`;
    },

    // ---- 5. NEW: stock anomalies -------------------------------------------

    /**
     * The pharmacy's daily check (spec §4.4), the exact counterpart of the drawer's
     * day-close variance: negative positions AND the events that took them there.
     * @returns {Promise<string>}
     */
    async renderStockAnomalies() {
        const rows = await this.readAll('v_stock_anomalies', {
            order: ['drug_name', 'business_date', 'event_id']
        });

        if (rows.length === 0) {
            return `${this.reportHeader('Anomalies de stock', 'Positions négatives et les événements qui les ont causées')}
                    ${placeholder('Aucune position négative',
                        'Rien à réconcilier : chaque position du registre est supérieure ou égale à zéro.')}`;
        }

        const body = rows.map(r => `
            <tr>
                <td>${escapeHtml(r.drug_name)}</td>
                <td class="text-center" style="color: var(--danger-600);">${escapeHtml(`${r.position} ${r.unit || ''}`.trim())}</td>
                <td>${escapeHtml(r.kind)}</td>
                <td class="text-center">${escapeHtml(r.delta)}</td>
                <td>${escapeHtml(r.business_date)}</td>
                <td>${escapeHtml(this.emailFor(r.collected_by))}</td>
            </tr>`).join('');

        const drugs = new Set(rows.map(r => r.drug_id));
        return `
            ${this.reportHeader('Anomalies de stock',
                `${drugs.size} médicament(s) en position négative`,
                'Une position négative signifie que des unités ont physiquement quitté l\'étagère sans que '
                + 'le registre les couvre — typiquement une vente hors ligne sur deux appareils. '
                + 'Comptez physiquement, puis enregistrez un ajustement motivé.',
                'La date affichée est celle de la vente d\'origine : une annulation restitue le stock sur la '
                + 'journée où la vente a eu lieu (spéc. §10.1).')}
            <table>
                <thead>
                    <tr>
                        <th>Médicament</th>
                        <th class="text-center">Position</th>
                        <th>Type d'événement</th>
                        <th class="text-center">Delta</th>
                        <th>Journée</th>
                        <th>Encaissé par</th>
                    </tr>
                </thead>
                <tbody>${body}</tbody>
            </table>`;
    },

    // ---- 6. NEW: receipt gaps ----------------------------------------------

    /**
     * Holes strictly below each block's high-water mark (spec §10).
     *
     * An unused TAIL of a live block is not a gap — it is tomorrow's receipts — and a
     * number consumed by an event parked in an OPEN conflict is not a gap either: it
     * is recoverable, and the admin must not be sent chasing ghosts through the paper
     * book. `v_receipt_gaps` applies both rules; this screen only renders them.
     * @returns {Promise<string>}
     */
    async renderReceiptGaps() {
        const [gaps, devices] = await Promise.all([
            this.readAll('v_receipt_gaps', { order: ['device_id', 'missing_receipt_no'] }),
            this.readAll('devices', { columns: 'id, name', order: ['id'] })
        ]);
        const deviceName = new Map(devices.map(d => [d.id, d.name]));

        if (gaps.length === 0) {
            return `${this.reportHeader('Numéros de reçu manquants', 'Contrôle de la séquence des carnets')}
                    ${placeholder('Aucun trou détecté',
                        'Aucun numéro manquant sous les numéros déjà émis.')}`;
        }

        /** @type {Map<string, number[]>} */
        const byDevice = new Map();
        for (const gap of gaps) {
            const key = gap.device_id || 'inconnu';
            const list = byDevice.get(key) || [];
            list.push(Number(gap.missing_receipt_no));
            byDevice.set(key, list);
        }

        const body = [...byDevice.entries()].map(([deviceId, numbers]) => `
            <tr>
                <td>${escapeHtml(deviceName.get(deviceId) || `Appareil inconnu (${String(deviceId).slice(0, 8)}…)`)}</td>
                <td class="text-center">${escapeHtml(numbers.length)}</td>
                <td>${escapeHtml(numbers.sort((a, b) => a - b).map(n => formatReceiptNo(n)).join(', '))}</td>
            </tr>`).join('');

        return `
            ${this.reportHeader('Numéros de reçu manquants',
                `${gaps.length} numéro(s) sans vente correspondante`,
                'Chaque numéro ci-dessous a été sauté sous un numéro déjà utilisé : cherchez-le dans le '
                + 'carnet de reçus papier. Les numéros en attente dans la file de conflits sont exclus — '
                + 'ils sont récupérables et ne sont pas des trous.',
                'La fin inutilisée d\'un carnet en cours n\'est pas un trou : ce sont les reçus de demain.')}
            <table>
                <thead>
                    <tr>
                        <th>Appareil</th>
                        <th class="text-center">Nombre</th>
                        <th>Numéros manquants</th>
                    </tr>
                </thead>
                <tbody>${body}</tbody>
            </table>`;
    },

    // ---- 7. NEW: price mismatches ------------------------------------------

    /**
     * The control that pays for the offline tradeoff (spec §5).
     *
     * The event carries the unit prices ACTUALLY CHARGED — that is the truth of the
     * transaction and what the printed receipt says — and the server records them and
     * flags divergence from the current list price rather than recomputing the total.
     * Recomputing would silently overwrite what a patient actually paid whenever an
     * offline device held a price list an admin has since changed.
     * @param {RangeBounds} bounds
     * @returns {Promise<string>}
     */
    async renderPriceMismatches(bounds) {
        const rows = await this.readAll('v_price_mismatches', {
            order: ['business_date', 'event_id'],
            filter: q => q.gte('business_date', bounds.fromDate).lte('business_date', bounds.toDate)
        });

        if (rows.length === 0) {
            return `${this.reportHeader('Écarts de prix', this.periodLabel(bounds, false))}
                    ${placeholder('Aucun écart de prix',
                        'Sur la période, chaque ligne a été facturée au prix de la liste actuelle.')}`;
        }

        const body = rows.map(r => `
            <tr>
                <td>${escapeHtml(r.business_date)}</td>
                <td>${escapeHtml(r.item_name || 'Article non nommé')}${r.item_is_active === false ? ' <em>(désactivé)</em>' : ''}</td>
                <td class="text-center">${escapeHtml(r.qty)}</td>
                <td class="text-right">${escapeHtml(fmtMoney(Number(r.charged_unit_price || 0)))}</td>
                <td class="text-right">${escapeHtml(fmtMoney(Number(r.list_unit_price || 0)))}</td>
                <td class="text-right" style="color: var(--danger-600);">
                    ${Number(r.delta) > 0 ? '+' : ''}${escapeHtml(fmtMoney(Number(r.delta || 0)))}
                </td>
                <td>${escapeHtml(this.emailFor(r.collected_by))}</td>
            </tr>`).join('');

        return `
            ${this.reportHeader('Écarts de prix', this.periodLabel(bounds, false),
                `${rows.length} ligne(s) facturée(s) à un prix différent de la liste actuelle.`,
                'Un écart n\'est pas nécessairement une faute : un appareil hors ligne peut avoir facturé '
                + 'au prix qu\'il connaissait avant une mise à jour tarifaire. Le montant facturé fait foi — '
                + 'c\'est ce que le patient a payé et ce que dit son reçu.')}
            <table>
                <thead>
                    <tr>
                        <th>Journée</th>
                        <th>Article</th>
                        <th class="text-center">Qté</th>
                        <th class="text-right">Prix facturé</th>
                        <th class="text-right">Prix liste actuel</th>
                        <th class="text-right">Écart</th>
                        <th>Encaissé par</th>
                    </tr>
                </thead>
                <tbody>${body}</tbody>
            </table>`;
    }
};

window.Reporting = Reporting;
